import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { ShieldStar, ArrowsClockwise } from "@phosphor-icons/react";

export default function Handover() {
    const { refresh } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            const { data } = await api.post("/handover/accept", { email, pin });
            localStorage.setItem("utal_token", data.token);
            toast.success(`Shift handed to ${data.user.email}`);
            await refresh();
            nav("/app/scanner", { replace: true });
        } catch (e) {
            const d = e.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Handover failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#101012] text-white relative">
            <div className="grain absolute inset-0" />
            <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-6">
                <div className="max-w-md w-full">
                    <div className="mb-8" data-testid="handover-brand">
                        <Brand to="/" variant="dark" size="md" tagline="SHIFT HANDOVER" taglineColor="text-white/50" dataTestId="handover-brand-logo" />
                    </div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary mb-2">· <ArrowsClockwise size={12} weight="bold" className="inline" /> INCOMING OFFICER</div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">{t("scannerAcceptShift")}</h1>
                    <p className="text-sm text-white/60 mb-6">Enter your email and 6-digit shift PIN. No password required.</p>

                    <Card className="p-6 rounded-sm bg-[#161618] border-[#2a2a2a]">
                        <form onSubmit={submit} className="space-y-4" data-testid="handover-form">
                            <div>
                                <Label className="text-xs tracking-widest uppercase font-bold text-white/70">{t("email")}</Label>
                                <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 bg-[#0f0f10] border-[#2a2a2a] text-white" data-testid="handover-email" />
                            </div>
                            <div>
                                <Label className="text-xs tracking-widest uppercase font-bold text-white/70">6-digit shift PIN</Label>
                                <Input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1.5 h-14 bg-[#0f0f10] border-[#2a2a2a] text-white text-center text-3xl tracking-[0.6em] mono" data-testid="handover-pin" />
                            </div>
                            {err && <div className="text-sm text-primary" data-testid="handover-error">{err}</div>}
                            <Button type="submit" disabled={busy || pin.length < 6} className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90" data-testid="handover-submit">
                                {busy ? "…" : "Take Shift"}
                            </Button>
                        </form>
                    </Card>
                    <div className="text-[10px] mono text-white/40 mt-4">
                        Officers set their PIN under Security · Shift PIN. Contact administration if lost.
                    </div>
                </div>
            </div>
        </div>
    );
}
